import React, { useState } from 'react';
import { getAuth, getMultiFactorResolver, TotpMultiFactorGenerator, PhoneMultiFactorGenerator, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db, googleProvider } from '../firebase'; // Make sure to import your Firestore instance
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import logo from './images/images.png';

export default function LoginArea() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otp, setOtp] = useState('');
  const [selectedFactorIndex, setSelectedFactorIndex] = useState(0);
  const [resolver, setResolver] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const [showSignUpNotification, setShowSignUpNotification] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setShowSignUpNotification(false);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (userCredential.user) {
        console.log('User logged in:', userCredential.user.uid);
        handleRedirect(userCredential.user);
      } else {
        throw new Error('No user returned from signInWithEmailAndPassword');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/multi-factor-auth-required') {
        const multiFactorResolver = getMultiFactorResolver(auth, error);
        setResolver(multiFactorResolver);
        setMfaRequired(true);
      } else if (error.code === 'auth/user-not-found') {
        // Email doesn't exist, show sign up notification
        setShowSignUpNotification(true);
      } else {
        setError('Invalid email or password. Please try again.');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      await handleRedirect(userCredential.user);
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (error.code !== 'auth/popup-closed-by-user') {
        setError('Google sign-in could not be completed. Please try again.');
      }
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedHint = resolver.hints[selectedFactorIndex];
      let multiFactorAssertion;

      if (selectedHint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(selectedHint.uid, otp);
      } else if (selectedHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        // Handle phone MFA (additional code needed for phone MFA)
      } else {
        throw new Error('Unsupported second factor');
      }

      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
      handleRedirect(userCredential.user);
    } catch (error) {
      setError('Invalid verification code. Please try again.');
      console.error(error);
    }
  };

  const handleRedirect = async (user) => {
    try {
      console.log('User UID:', user.uid);

      let idTokenResult = await user.getIdTokenResult(true);
      
      // Isak owns sales workflows, so his default landing page is the sales portal.
      // The route also allows his email directly in case his sales claim is stale.
      if (user.email?.toLowerCase() === 'isak@lended.ai') {
        navigate('/sales-portal');
        return;
      }

      // Ben goes straight to the Ben portal by email, without an admin claim.
      const benPortalEmails = ['ben@lended.ai'];
      if (benPortalEmails.includes(user.email?.toLowerCase())) {
        navigate('/ben-portal');
        return;
      }

      if (idTokenResult.claims.admin) {
        navigate('/admindashboard');
        return;
      }

      if (idTokenResult.claims.salesRep) {
        navigate('/sales-portal');
        return;
      }

      // Older rep records may predate automatic Auth role provisioning. The
      // server only activates the role when this exact signed-in email already
      // has an active salesReps record.
      const activation = await httpsCallable(functions, 'activateSalesRepAccess')();
      if (activation.data?.activated) {
        idTokenResult = await user.getIdTokenResult(true);
        if (idTokenResult.claims.salesRep) {
          navigate('/sales-portal');
          return;
        }
      }

      // Provisioned brokers set their own password through an invitation link.
      // On first login, attach the role to the exact matching broker record and
      // mark the invitation activated before routing to the portal.
      const brokerActivation = await httpsCallable(functions, 'activateBrokerAccess')();
      if (brokerActivation.data?.activated) {
        await user.getIdTokenResult(true);
        navigate('/broker-portal');
        return;
      }

      // Check if the user is associated with a bank
      const banksRef = collection(db, 'banks');
      const bankQuery = query(banksRef, where('userId', '==', user.uid));
      const bankSnapshot = await getDocs(bankQuery);

      if (!bankSnapshot.empty) {
        navigate('/bank-portal');
        return;
      }

      // Check if the user is a broker
      const brokersRef = collection(db, 'brokers');
      const brokerQuery = query(brokersRef, where('userId', '==', user.uid));
      const brokerSnapshot = await getDocs(brokerQuery);

      if (!brokerSnapshot.empty) {
        navigate('/broker-portal');
        return;
      }

      // Regular user - check for loan application
      const loanAppsRef = collection(db, 'loanApplications');
      const loanAppQuery = query(loanAppsRef, where('userId', '==', user.uid));
      const loanAppSnapshot = await getDocs(loanAppQuery);

      if (!loanAppSnapshot.empty) {
        // Get the first loan application
        const loanApp = loanAppSnapshot.docs[0];
        const loanData = loanApp.data();
        
        // Check if it's a working capital loan
        if (loanData.loanPurpose && 
            (loanData.loanPurpose === 'workingCapital' || 
             loanData.loanPurpose === 'workingCapital-startup' ||
             loanData.loanPurpose === 'workingCapital-nonsba')) {
          // Redirect to shared upload with the loan ID
          navigate(`/shared-upload/${loanApp.id}`);
        } else {
          // For other loan types, go to regular portal
          navigate('/portal');
        }
      } else {
        navigate('/entryform');
      }

    } catch (error) {
      console.error('Error during redirection:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      setError('An error occurred during login. Please try again.');
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-blue-900 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-10 bg-white p-8 rounded-lg shadow-lg">
        <div>
          <img className="mx-auto h-12 w-auto" src={logo} alt="Your Company" />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Sign in to your account
          </h2>
        </div>
        {!mfaRequired ? (
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {showSignUpNotification && (
              <div className="text-sm text-center text-red-600">
                This email is not registered. Please sign up for an account.
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-500"
                    onClick={() => navigate('/signup')} // Adjust this path as needed
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            )}

            {error && <div className="text-sm text-red-600">{error}</div>}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Sign in
              </button>
            </div>

            <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-500">or</span></div></div>
            <button type="button" onClick={handleGoogleSignIn} className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
              <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.06H12v3.9h5.38a4.6 4.6 0 0 1-2 3.02v2.53h3.24c1.9-1.75 2.98-4.33 2.98-7.39Z"/><path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.38l-3.24-2.53c-.9.6-2.05.96-3.38.96-2.6 0-4.81-1.76-5.6-4.12H3.05v2.6A10 10 0 0 0 12 22Z"/><path fill="#FBBC05" d="M6.4 13.93A6 6 0 0 1 6.09 12c0-.67.12-1.32.31-1.93v-2.6H3.05A10 10 0 0 0 2 12c0 1.61.39 3.14 1.05 4.53l3.35-2.6Z"/><path fill="#EA4335" d="M12 5.95c1.47 0 2.79.5 3.82 1.49l2.87-2.87A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.95 5.47l3.35 2.6c.79-2.36 3-4.12 5.6-4.12Z"/></svg>
              Continue with Google
            </button>

            <div className="text-center">
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-500"
                onClick={handleForgotPassword}
              >
                Forgot password?
              </button>
            </div>
          </form>
        ) : (
          <form className="space-y-6" onSubmit={handleMfaSubmit}>
            <div>
              <label htmlFor="verification-code" className="sr-only">
                Verification Code
              </label>
              <input
                id="verification-code"
                name="verification-code"
                type="text"
                autoComplete="one-time-code"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter verification code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </div>

            {error && <div className="text-sm text-red-600">{error}</div>}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Verify and Sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
